import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { formatGroupTime, resolveGroupHosts } from "@/lib/eventGroups";
import type { Event, EventGroupAvailability } from "@/types/event";
import { Clock3, MapPin } from "lucide-react";

interface DepartureScheduleProps {
  event: Event;
  groups: EventGroupAvailability[];
  selectedGroupId: string | null;
  onSelect?: (groupId: string) => void;
  compact?: boolean;
  bookedGroupIds?: string[];
}

const groupHosts = (event: Event, group: EventGroupAvailability) =>
  resolveGroupHosts(group, {
    primary: event.profiles ? { ...event.profiles, id: event.host_id ?? "event-host" } : null,
    secondary: event.secondary_profiles ? { ...event.secondary_profiles, id: event.secondary_host_id ?? "event-cohost" } : null,
  });

const DepartureSchedule = ({ event, groups, selectedGroupId, onSelect, compact = false, bookedGroupIds = [] }: DepartureScheduleProps) => {
  if (groups.length === 0) return null;
  const sharedVenue = groups.every((group) => (group.meeting_venue || event.venue) === (groups[0].meeting_venue || event.venue));

  const isInteractive = Boolean(onSelect) && bookedGroupIds.length === 0;
  return (
    <section className={cn("mb-5", compact && "mb-4")} aria-labelledby={compact ? undefined : "departures-heading"}>
      {!compact && <h2 id="departures-heading" className="mb-2 text-lg font-semibold text-foreground">Departures</h2>}
      {sharedVenue && !compact && (
        <p className="mb-2 flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 shrink-0" />
          {groups[0].meeting_venue || event.venue}
        </p>
      )}
      <div className="divide-y divide-border border-y border-border">
        {groups.map((group) => {
          const hosts = groupHosts(event, group);
          const isFull = group.spots_remaining <= 0;
          const selected = selectedGroupId === group.group_id;
          const isBooked = bookedGroupIds.includes(group.group_id);
          return (
            <button
              type="button"
              key={group.group_id}
              onClick={() => !isFull && isInteractive && onSelect?.(group.group_id)}
              disabled={isFull || !isInteractive}
              className={cn(
                "flex w-full items-start gap-3 py-3 text-left disabled:cursor-default",
                selected && "bg-accent/60",
                isFull && !isBooked && "opacity-70",
                compact && "py-2.5"
              )}
              aria-pressed={isInteractive ? selected : undefined}
            >
              {isInteractive && (
                <span className={cn("mt-1.5 h-4 w-4 shrink-0 rounded-full border border-border", selected && "border-primary bg-primary ring-2 ring-background")} />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <p className="font-semibold text-foreground">
                    {group.name} · {formatGroupTime(group.start_time, event.timezone)}
                  </p>
                  <span className={cn("text-xs font-medium", isFull ? "text-muted-foreground" : "text-foreground")}>
                    {isBooked ? "Your departure" : isFull ? "Full" : `${group.spots_remaining} ${group.spots_remaining === 1 ? "spot" : "spots"} left`}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                  {group.end_time && (
                    <span className="flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" />Until {formatGroupTime(group.end_time, event.timezone)}</span>
                  )}
                  {hosts.length > 0 && (
                    <span className="flex items-center gap-1.5">
                      <span>with {hosts.map((host) => host?.first_name).filter(Boolean).join(" & ")}</span>
                      <span className="flex -space-x-1">
                        {hosts.map((host, index) => (
                          <Avatar key={host?.id ?? index} className="h-5 w-5 border border-background">
                            <AvatarImage src={host?.avatar_url ?? ""} alt="" />
                            <AvatarFallback className="text-[9px]">{host?.first_name?.[0] ?? "?"}</AvatarFallback>
                          </Avatar>
                        ))}
                      </span>
                    </span>
                  )}
                </div>
                {!sharedVenue && !compact && (
                  <p className="mt-1 flex items-start gap-1 text-sm text-muted-foreground">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {group.meeting_venue || event.venue}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default DepartureSchedule;